// React Component
import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Cookies from 'js-cookie';
import { markAuthSyncGracePeriod, removeAuthCookie, setAuthCookie, persistAuthTokens } from '../../utils/authUtils';
import {
    navigateToAutomationRedirect,
    peekAutomationRedirect,
} from '../../utils/automationRedirect';

// App Component
import { Buttons } from '../../components'
import { AuthLayout } from '../../layouts'
import { Routes } from '../../routes'
import ButtonSocialeNetwork from '../../features/auth/ButtonSocialeNetwork';
import { useAppDispatch } from '../../stores';
import { login } from '../../stores';
import { iSessions } from '../../features/auth';
import { FamlaServices } from '../../services';
import { validateInvitationToken, InvitationTokenResult, InvitationTokenData } from '../../utils/invitationToken';
import { peekCollectInviteReturn, clearCollectInviteReturnStorage } from '../../utils/collectInviteRedirect';
import { isPrivateEmail } from '../../utils/emailUtils';
import { withStSignupAllow } from '../../utils/stSignupAccess';
import {
    maybeRedirectStInviteFromPublicApp,
    maybeRedirectLoggedInStUser,
} from '../../utils/stCorporateRedirect';
import {
    getMarketplaceToken,
    shouldResumeMarketplaceFlow,
    startMarketplaceFlow,
} from '../../utils/marketplaceSession';

//interface
interface ChildProps {
    google: boolean
}

const AuthViewComponent = ({ google }: ChildProps) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [isAuthenticating, setIsAuthenticating] = React.useState(false);
    const [ssoError, setSsoError] = React.useState<string | null>(null);

    // [INVITATION PROCESSUS] Récupérer le process_id depuis l'URL ou sessionStorage
    const urlParams = new URLSearchParams(window.location.search);
    
    // [GOOGLE SSO ERROR] Vérifier si une erreur Google SSO est présente dans l'URL
    React.useEffect(() => {
        const errorType = urlParams.get("error");
        const errorMessage = urlParams.get("message");
        if (errorType === "google_personal_email" && errorMessage) {
            setSsoError(decodeURIComponent(errorMessage));
            // Nettoyer l'URL
            window.history.replaceState({}, document.title, window.location.pathname);
        }
    }, []);
    let invitedProcessId = urlParams.get("invited_process");
    if (invitedProcessId) {
        // Stocker dans sessionStorage pour le préserver pendant la navigation
        sessionStorage.setItem('invited_process_id', invitedProcessId);
    } else {
        // Vérifier dans sessionStorage (pour les utilisateurs qui viennent de s'inscrire)
        invitedProcessId = sessionStorage.getItem('invited_process_id');
    }

    const authNavigation = (path: string) => {
        // [PRÉSERVER PARAMÈTRE] Conserver le paramètre invited_process lors de la navigation
        const queryParam = invitedProcessId ? `?invited_process=${invitedProcessId}` : '';
        if (path === "login") {
            navigate(`${Routes.AUTH_LOGIN}${queryParam}`)
        }
        else {
            navigate(withStSignupAllow(`${Routes.AUTH_SIGNIN}${queryParam}`))
        }
    }

    const getCookie = (name: string) => {
        return Cookies.get(name);
    };

    const loadSession = useCallback(async (token: string) => {
        try {
            setIsAuthenticating(true);
            let data = await FamlaServices("api_host", `/auth/me`, "GET", "", token);
            if (data.status === 200) {
                const session: iSessions = data.body.data;
                // [VÉRIFICATION EMAIL] Vérifier que l'utilisateur a vérifié son email
                if (session.user && !session.user.verified) {
                    // Email non vérifié → empêcher la connexion
                    // Supprimer les cookies pour forcer une nouvelle authentification
                    removeAuthCookie('access_token');
                    removeAuthCookie('refresh_token');
                    removeAuthCookie('logged_in');
                    setIsAuthenticating(false);
                    // Ne pas naviguer, rester sur la page de login
                    return;
                }

                // [ST REDIRECT] Compte @st.com connecté sur famla.ai → portail ST
                if (maybeRedirectLoggedInStUser(session.user?.email)) {
                    return;
                }
                
                // [INVITATION TOKEN] Récupérer le process_id depuis sessionStorage (validé via token)
                // Le token a été validé dans useEffect et les informations stockées dans sessionStorage
                let processIdFromUrl = sessionStorage.getItem('invited_process_id');
                const invitationToken = sessionStorage.getItem('invitation_token');
                if (processIdFromUrl || invitationToken) {
                    console.log('🔗 [AuthViewComponent] Invitation detected in sessionStorage:', { 
                        processId: processIdFromUrl, 
                        hasToken: !!invitationToken 
                    });
                }
                
                dispatch(login({ token: token, session: session, user: session.user }));
                persistAuthTokens(token);
                markAuthSyncGracePeriod();

                // [ANNONCES] Stocker les annonces en attente (même logique que le login classique)
                const pendingAnnouncements: string[] = (session.user as any)?.pending_announcements ?? [];
                sessionStorage.setItem('pending_announcements', JSON.stringify(pendingAnnouncements));
                
                const collectReturn = peekCollectInviteReturn(
                    new URLSearchParams(window.location.search).get('redirect'),
                );

                const urlMarketplaceToken = new URLSearchParams(window.location.search).get('token');
                const redirectParam = new URLSearchParams(window.location.search).get('redirect');
                const tokenFromRedirect = redirectParam
                    ? new URL(redirectParam, window.location.origin).searchParams.get('token')
                    : null;
                const marketplaceTokenFromUrl = urlMarketplaceToken || tokenFromRedirect;

                if (marketplaceTokenFromUrl) {
                    startMarketplaceFlow(marketplaceTokenFromUrl);
                    navigate(
                        `/microsoft-purchase?token=${encodeURIComponent(marketplaceTokenFromUrl)}`,
                        { replace: true },
                    );
                    return;
                }

                if (shouldResumeMarketplaceFlow()) {
                    const marketplaceToken = getMarketplaceToken();
                    if (marketplaceToken) {
                        navigate(
                            `/microsoft-purchase?token=${encodeURIComponent(marketplaceToken)}`,
                            { replace: true },
                        );
                        return;
                    }
                }

                const automationReturn = peekAutomationRedirect(
                    new URLSearchParams(window.location.search).get('redirect'),
                );
                if (session.company_id !== null && automationReturn) {
                    navigateToAutomationRedirect(automationReturn);
                    return;
                }

                // [VÉRIFICATION COMPANY] Si pas de company, rediriger vers création company (même si invité)
                if (session.company_id === null) {
                    console.log('🔗 [AuthViewComponent] No company, redirecting to create company');
                    // Si invitation présente, elle sera préservée dans sessionStorage pour redirection après création
                    navigate(Routes.CREATE_MY_COMPANY);
                } else if (collectReturn) {
                    clearCollectInviteReturnStorage();
                    navigate(collectReturn, { replace: true });
                } else if (processIdFromUrl) {
                    // [INVITATION + COMPANY] Si invitation présente et company existe, rediriger vers processus invité
                    console.log('🔗 [AuthViewComponent] Has company and invitation, redirecting to invited process:', processIdFromUrl);
                    // NE PAS nettoyer sessionStorage ici - laisser AuthGuard le faire après redirection réussie
                    navigate(`${Routes.PROCESS_INTERVIEW}${processIdFromUrl}`, { replace: true });
                } else {
                    // [PAS D'INVITATION + COMPANY] Redirection normale vers homepage
                    console.log('🔗 [AuthViewComponent] Has company but no invitation, redirecting to home');
                    navigate(Routes.HOMEVIEW);
                }
                // Note: on ne désactive pas isAuthenticating ici car la navigation va changer de page
            } else if (data.status === 401) {
                // [ERREUR AUTH] Email non vérifié ou session invalide
                // Supprimer les cookies pour forcer une nouvelle authentification
                removeAuthCookie('access_token');
                removeAuthCookie('refresh_token');
                removeAuthCookie('logged_in');
                setIsAuthenticating(false);
                if (google) {
                    window.location.reload()
                }
            } else {
                setIsAuthenticating(false);
                if (google) {
                    window.location.reload()
                }
            }
        } catch (e) {
            // En cas d'erreur, supprimer les cookies pour sécurité
            removeAuthCookie('access_token');
            removeAuthCookie('refresh_token');
            removeAuthCookie('logged_in');
            setIsAuthenticating(false);
            return false;
        }

    }, [dispatch, google, navigate]);

    React.useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const invitationToken = urlParams.get("invitation_token");
        const inviteFlow = urlParams.get("invite");
        const invitedProcessParam = urlParams.get("invited_process");
        const invitedEmailFromUrl = urlParams.get("invited_email")?.trim();

        // [ST REDIRECT] User @st.com sur instance grand public → st.famla.ai
        if (maybeRedirectStInviteFromPublicApp({ invitedEmail: invitedEmailFromUrl, invitationToken })) {
            return;
        }

        // [INVITATION EMAIL] Liens owner / teammate — avec invited_email : compte existant → login
        if (inviteFlow === "1" && !invitationToken) {
            const invitedEmailEarly = invitedEmailFromUrl;
            if (
                invitedEmailEarly &&
                !isPrivateEmail(invitedEmailEarly)
            ) {
                FamlaServices(
                    "api_host",
                    `/auth/invite/account-exists?email=${encodeURIComponent(invitedEmailEarly)}`,
                    "GET",
                    "",
                    ""
                )
                    .then((data: { status: number; body?: { data?: { account_exists?: boolean } } }) => {
                        if (data.status === 200 && data.body?.data?.account_exists) {
                            navigate(Routes.AUTH_LOGIN, {
                                replace: true,
                                state: { fromInviteSignup: true, email: invitedEmailEarly },
                            });
                        } else {
                            navigate(
                                withStSignupAllow(
                                    `${Routes.AUTH_SIGNIN}?invite=1&invited_email=${encodeURIComponent(invitedEmailEarly)}`
                                ),
                                { replace: true }
                            );
                        }
                    })
                    .catch(() => {
                        navigate(
                            withStSignupAllow(
                                `${Routes.AUTH_SIGNIN}?invite=1&invited_email=${encodeURIComponent(invitedEmailEarly)}`
                            ),
                            { replace: true }
                        );
                    });
                return;
            }
            navigate(withStSignupAllow(`${Routes.AUTH_SIGNIN}?invite=1`), { replace: true });
            return;
        }
        if (invitedProcessParam && !invitationToken) {
            sessionStorage.setItem('invited_process_id', invitedProcessParam);
            const invitedEmailLegacy = invitedEmailFromUrl;
            const signupQuery = invitedEmailLegacy
                ? `invited_process=${encodeURIComponent(invitedProcessParam)}&invited_email=${encodeURIComponent(invitedEmailLegacy)}`
                : `invited_process=${encodeURIComponent(invitedProcessParam)}`;
            navigate(withStSignupAllow(`${Routes.AUTH_SIGNIN}?${signupQuery}`), { replace: true });
            return;
        }

        // [INVITATION TOKEN] Vérifier si un token d'invitation est présent dans l'URL
        if (invitationToken) {
            validateInvitationToken(invitationToken).then((result: InvitationTokenResult) => {
                if (result.success && result.data) {
                    const data: InvitationTokenData = result.data;
                    sessionStorage.setItem('invited_process_id', data.process_id);
                    sessionStorage.setItem('invited_user_email', data.user_email);
                    sessionStorage.setItem('invitation_token', invitationToken);
                    console.log('🔗 [AuthViewComponent] Invitation token validated, stored process_id and email in sessionStorage');

                    // [ST REDIRECT] Token ciblant @st.com sur famla.ai → portail ST
                    if (maybeRedirectStInviteFromPublicApp({ invitedEmail: data.user_email, invitationToken })) {
                        return;
                    }

                    if ('user_exists' in data && data.user_exists === false) {
                        console.log('🔗 [AuthViewComponent] User does not have an account, redirecting to signup');
                        window.history.replaceState({}, document.title, window.location.pathname);
                        navigate(
                            withStSignupAllow(`${Routes.AUTH_SIGNIN}?invitation_token=${invitationToken}`),
                            { replace: true }
                        );
                        return;
                    }
                    console.log('🔗 [AuthViewComponent] User already has account, redirecting to login');
                    window.history.replaceState({}, document.title, window.location.pathname);
                    navigate(Routes.AUTH_LOGIN, { replace: true });
                } else {
                    console.warn('🔗 [AuthViewComponent] Invalid or expired invitation token:', result.error);
                }
            }).catch((error: Error) => {
                console.error('🔗 [AuthViewComponent] Error validating invitation token:', error);
            });
        } else {
            const invitedProcessId = urlParams.get("invited_process");
            if (invitedProcessId) {
                sessionStorage.setItem('invited_process_id', invitedProcessId);
                console.log('🔗 [AuthViewComponent] Invited process ID stored in sessionStorage from URL (legacy):', invitedProcessId);
            }
        }
        
        // 1. Vérifier si un token est présent dans l'URL (redirection depuis famla-ai-homepage)
        const tokenFromUrl = urlParams.get('token');
        
        if (tokenFromUrl) {
            console.log('🔑 Token trouvé dans l\'URL, authentification automatique...');
            // Stocker le token dans les cookies
            setAuthCookie('access_token', tokenFromUrl, { expires: 1 });
            // Nettoyer l'URL pour enlever le token (mais garder invitation_token si présent)
            const invitationTokenKeep = urlParams.get("invitation_token");
            const newUrl = invitationTokenKeep 
                ? `${window.location.pathname}?invitation_token=${invitationTokenKeep}`
                : window.location.pathname;
            window.history.replaceState({}, document.title, newUrl);
            // Charger la session avec ce token
            loadSession(tokenFromUrl);
            return;
        }
        
        // 2. Vérifier le cookie access_token (pour tous les SSO: Google, Microsoft, ST)
        const accessToken = getCookie('access_token');
        if (accessToken) {
            console.log('🔑 Token trouvé dans les cookies, chargement de la session...');
            loadSession(accessToken)
        }
        // console.log('relod===');
    }, [loadSession, navigate])


    // Afficher un loader pendant l'authentification automatique
    if (isAuthenticating) {
        return (
            <AuthLayout>
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '300px',
                    gap: '20px'
                }}>
                    <div className="spinner-border" role="status" style={{ width: '3rem', height: '3rem', color: '#0B0B0C' }}>
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <h5 className="fw-bold" style={{ color: '#0B0B0C' }}>{t('views.auth.authenticating') || 'Connexion en cours...'}</h5>
                    <p style={{ color: '#555', opacity: 0.9 }}>
                        {t('views.auth.pleaseWait') || 'Veuillez patienter'}
                    </p>
                </div>
            </AuthLayout>
        );
    }

    return (
        <AuthLayout>
            <h4 className="fw-bold" style={{ color: '#0B0B0C' }}>{t('views.auth.getStarted')}</h4>
            
            {/* [GOOGLE SSO ERROR] Afficher l'erreur si présente */}
            {ssoError && (
                <div className="alert alert-warning w-50 mt-3" role="alert">
                    <i className="fa-solid fa-triangle-exclamation me-2"></i>
                    {t('views.auth.googleSsoProOnly') || ssoError}
                    <button 
                        type="button" 
                        className="btn-close float-end" 
                        onClick={() => setSsoError(null)}
                        aria-label="Close"
                    ></button>
                </div>
            )}
            
            {google && (
                <div className="w-50 mt-4">
                    <ButtonSocialeNetwork t={t} />
                </div>
            )}
            <Buttons
                name={t('views.auth.logIn')}
                onClick={() => authNavigation("login")}
                isLoading={false}
                btnClass="btn-theme w-50 mt-4"
                labelClass="text-white fw-bold"
                styleProps={{ height: 45 }}
                arrowVisible={false}
                arrowColor=''
            />

            <Buttons
                name={t('views.auth.signUp')}
                onClick={() => authNavigation("signin")}
                isLoading={false}
                btnClass="btn-theme w-50 mt-4"
                labelClass="text-white fw-bold"
                styleProps={{ height: 45 }}
                arrowVisible={false}
                arrowColor=''
            />
            <div className='mt-mob'></div>
        </AuthLayout>
    )
}
export default AuthViewComponent